import type { BrowserCricket } from "./crickets";
import {
  AI_CRICKET_IDS,
  CRICKETS,
  findCricket,
  hasAiConfig,
} from "./crickets";
import type { Dict } from "./i18n";
import { slotTheme } from "./theme";

/**
 * Per-cricket controls in the HUD, immediately beside the cricket they change.
 *
 * Deliberately not a separate roster panel. Which brain is driving which cricket
 * is the single most important fact about a match, and a picker parked in a side
 * column makes the viewer read it as configuration rather than as part of the
 * scoreboard. Sitting under the HP bar of the cricket it controls, it answers
 * "who is that?" at the same glance as "how is it doing?".
 *
 * Only the crickets on the bench are offered, so the control is a swap rather
 * than a menu of the current state.
 */
export function HudPicker({
  slot,
  currentId,
  onPick,
  d,
}: {
  slot: 0 | 1;
  currentId: string;
  onPick: (id: string) => void;
  d: Dict;
}) {
  const bench = benchOptions(currentId, d);
  const theme = slotTheme(slot);

  return (
    <div className="hud-picker">
      {bench.map((cricket) => (
        <button
          key={cricket.id}
          className="hud-pick"
          onClick={() => onPick(cricket.id)}
          title={cricket.blurb}
        >
          <span className="sw" style={{ background: cricket.isAi ? "var(--amber)" : theme.dim }} />
          {cricket.label}
        </button>
      ))}
    </div>
  );
}

interface BenchOption {
  readonly id: string;
  readonly label: string;
  readonly blurb: string;
  readonly isAi: boolean;
}

/**
 * The crickets not currently in this slot.
 *
 * Configured AI slots are listed with their ring names. Unconfigured AI slots
 * stay off the bench so a missing key cannot become a failed match mid-swap.
 */
function benchOptions(currentId: string, d: Dict): BenchOption[] {
  const all: BrowserCricket[] = [...CRICKETS];
  for (const id of AI_CRICKET_IDS) {
    if (!hasAiConfig(id)) continue;
    try {
      all.push(findCricket(id));
    } catch {
      // Slot listed as configured but unreadable — skip rather than crash the HUD.
    }
  }

  return all
    .filter((c) => c.id !== currentId)
    .map((c) => ({
      id: c.id,
      label: c.persona,
      blurb:
        c.blurbKey === "collector"
          ? d.blurbCollector
          : c.blurbKey === "scout"
            ? d.blurbScout
            : c.blurbKey === "hunter"
              ? d.blurbHunter
              : d.aiBlurb,
      isAi: (AI_CRICKET_IDS as readonly string[]).includes(c.id),
    }));
}
